import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { TechAuthProvider } from './contexts/TechAuthContext';
import { initializeStax } from './lib/stax';
import { getPortalContext } from './lib/portal';
import { applyTechBrandingCssVariables, getTechBranding } from './lib/tech-branding';
import './index.css';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const portal = getPortalContext();

// Apply brand color variables for tech and membership portals (used by CSS classes like bg-brand-gradient).
if (portal.portal === 'tech' || portal.portal === 'memberships') {
  applyTechBrandingCssVariables(getTechBranding(portal.companyId));
}

// Enable a minimal PWA experience (service worker + caching) only for production tech.* portals.
if (
  import.meta.env.PROD &&
  portal.portal === 'tech' &&
  typeof window !== 'undefined' &&
  window.location.hostname.toLowerCase().startsWith('tech.') &&
  'serviceWorker' in navigator
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

// Initialize Stax client (host-aware). This is a lightweight in-browser config used by some
// client-side utilities. Tokenization for enrollment uses useStax + StaxJs separately.
const companyId = portal.companyId || 'hales-ac';
let staxApiKey, staxWebPaymentsToken, staxMerchantId;

if (companyId === 'hales-ac') {
  staxApiKey = import.meta.env.VITE_STAX_HALES_API_KEY;
  staxWebPaymentsToken = import.meta.env.VITE_STAX_HALES_WEB_PAYMENTS_TOKEN;
  staxMerchantId = import.meta.env.VITE_STAX_HALES_MERCHANT_ID;
} else if (companyId === 'climate') {
  staxApiKey = import.meta.env.VITE_STAX_CLIMATE_API_KEY;
  staxWebPaymentsToken = import.meta.env.VITE_STAX_CLIMATE_WEB_PAYMENTS_TOKEN;
  staxMerchantId = import.meta.env.VITE_STAX_CLIMATE_MERCHANT_ID;
} else {
  staxApiKey = import.meta.env.VITE_STAX_ABRAHAM_API_KEY;
  staxWebPaymentsToken = import.meta.env.VITE_STAX_ABRAHAM_WEB_PAYMENTS_TOKEN;
  staxMerchantId = import.meta.env.VITE_STAX_ABRAHAM_MERCHANT_ID;
}

initializeStax({
  apiKey: staxApiKey,
  publicKey: staxWebPaymentsToken,
  merchantId: staxMerchantId,
  baseUrl: 'https://apiprod.fattlabs.com',
});

function AppAuthProviders({ children }: { children: React.ReactNode }) {
  // Tech and Memberships portals both use TechAuthProvider for technician login
  if (portal.portal === 'tech' || portal.portal === 'memberships') {
    return <TechAuthProvider>{children}</TechAuthProvider>;
  }

  return <AuthProvider>{children}</AuthProvider>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppAuthProviders>
          <App />
          <Toaster position="top-right" />
        </AppAuthProviders>
      </Router>
    </QueryClientProvider>
  </React.StrictMode>
);
